
<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { $api } from '@/services';
import { useRoute } from 'vue-router';
import Loading from '@/components/common/Loading.vue';
import ListQuestion from './ListQuestion.vue';
import Pie from './chart/Pie.vue';
import TextList from './chart/TextList.vue';
import Bar from './chart/Bar.vue';
import StackedBar from './chart/StackedBar.vue';
import Multiple from './question/Multiple.vue';
import Checkbox from './question/Checkbox.vue';
import Grid from './question/Grid.vue';
import TextEntry from './question/TextEntry.vue';
import Multiselect from '@suadelabs/vue3-multiselect';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import UpdateModal from './modal/UpdateModal.vue';
import DaModalFrame from '@/components/common/daModalFrame/DaModalFrame.vue';
import { useRouter } from 'vue-router';
import Section from './question/Section.vue';
import SendToParent from './modal/SendToParent.vue';
import CheckboxTemplate from '@/components/elements/checkbox/Checkbox.vue';

const modalIsOpen = ref(false);
const modalType = ref('');
const route = useRoute();
const router = useRouter();
const id = route.params.id;
const survey = ref(null);
const isLoading = ref(true);
const surveyResult = ref(null);
const indexNumber = ref(1);
const parentIndexNumber = ref(1);
const answers = ref({});
const questionAnswersCache = ref({});
const selectedGridRow = ref(null);
const parentsSurvey = ref([]);
const parentSelected = ref(null);
const surveyResultDetail = ref(null);
const tabs = computed(() => {
	let data = [
		{
			name: 'Câu hỏi',
		},
	];
	if (surveyResult.value?.total_submitted > 0) {
		data.push({
			name: 'Câu trả lời',
		});
	}
	return data;
});
const tabSelected = ref(0);
const tabAnswer = ref([
	{
		name: 'Bản tóm tắt',
	},
	{
		name: 'Câu hỏi',
	},
	{
		name: 'Cá nhân',
	},
]);
const tab = ref(0);
const showListParent = ref(false);
const listSurveyByParent = ref([
	{
		id: '1',
		display_name: 'Phụ huynh 1',
	},
	{
		id: '2',
		display_name: 'Phụ huynh 2',
	},
]);
const type = ref('');
const sideOverShow = ref(false);
const surveyQuestions = ref([]);

onMounted(() => {
	fetchSurveyDetail();
	fetchSurveyResult();
});

watch(tab, (val) => {
	if (val === 1) {
		fetchAnswerOfQuestion();
		if (!parentsSurvey.value.length) fetchParentsSurvey();
	}
	if (val === 2) {
		if (!parentsSurvey.value.length) fetchParentsSurvey();
	}
});

watch(indexNumber, () => {
	fetchAnswerOfQuestion();
});

watch(parentSelected, () => {
	fetchSurveyResultByParent();
});

watch(showListParent, () => {
	fetchListSurveyByParent();
});

const fetchListSurveyByParent = async () => {
	const res = await $api.survey.listSurveyByParent(id);
	listSurveyByParent.value = res.data.data?.students ?? [];
};

const fetchSurveyResultByParent = async () => {
	if (!parentSelected.value?.id) {
		surveyResultDetail.value = [];
		return;
	}
	const res = await $api.survey.getSurveyResultByParent(
		id,
		parentSelected.value.id
	);
	const payload = res.data?.data ?? res.data;
	surveyResultDetail.value = payload?.survey?.questions ?? [];
};

const fetchSurveyDetail = async () => {
	const res = await $api.survey.getSurveyDetail(id);
	survey.value = res.data.data;
	surveyQuestions.value = survey.value.questions.filter(
		(question) => question.question_type !== 'section'
	);
	isLoading.value = false;
};

const fetchSurveyResult = async () => {
	const res = await $api.survey.getSurveyResult(id);
	surveyResult.value = res.data.data;
};

const setDefaultGridRow = () => {
	if (answers.value?.question_type !== 'grid') {
		selectedGridRow.value = null;
		return;
	}
	const groups = answers.value?.response_groups;
	const contentRows =
		surveyQuestions.value[indexNumber.value - 1]?.content?.rows;
	if (Array.isArray(groups) && groups.length > 0) {
		selectedGridRow.value = groups[0].row;
	} else if (Array.isArray(contentRows) && contentRows.length > 0) {
		selectedGridRow.value = contentRows[0];
	} else {
		selectedGridRow.value = null;
	}
};

const fetchAnswerOfQuestion = async () => {
	const question = surveyQuestions.value[indexNumber.value - 1];
	if (!question?.id) return;

	const questionId = question.id;
	const cached = questionAnswersCache.value[questionId];
	if (cached) {
		answers.value = cached;
		if (answers.value?.question_type === 'grid') setDefaultGridRow();
		return;
	}

	const res = await $api.survey.getAnswerOfQuestion(id, questionId);
	answers.value = res.data.data;
	questionAnswersCache.value[questionId] = answers.value;
	if (answers.value?.question_type === 'grid') setDefaultGridRow();
};

const fetchParentsSurvey = async () => {
	const res = await $api.survey.getParentsSurvey(id);
	const rows = res.data.data ?? [];
	parentsSurvey.value = rows.map((item) => ({
		...item,
		text: item.parent?.label ?? '',
	}));
	parentSelected.value = parentsSurvey.value[0] ?? null;
};
const decreaseParent = () => {
	if (parentIndexNumber.value > 1) {
		parentIndexNumber.value--;
	}
};
const decrease = () => {
	if (indexNumber.value > 1) {
		indexNumber.value--;
	}
};

const increase = () => {
	if (indexNumber.value < survey.value.questions.length) {
		indexNumber.value++;
	}
};

const increaseParent = () => {
	if (parentIndexNumber.value < parentsSurvey.value.length) {
		parentIndexNumber.value++;
	}
};

const openUpdateModal = (typeModal) => {
	modalType.value = typeModal;
	modalIsOpen.value = true;
};

const selectedGridRowData = computed(() => {
	const groups = answers.value?.response_groups;
	if (answers.value?.question_type !== 'grid' || !selectedGridRow.value) {
		return null;
	}
	if (!Array.isArray(groups)) return null;
	return groups.find((g) => g.row === selectedGridRow.value) ?? null;
});

const checkboxParentAnswerRows = computed(() => {
	const groups = answers.value?.response_groups;
	if (answers.value?.question_type !== 'checkbox' || !Array.isArray(groups)) {
		return [];
	}
	/** parentId -> { parent, Set<optionLabel> } */
	const byParent = new Map();
	for (const group of groups) {
		const label = group?.option;
		if (!label) continue;
		for (const parent of group.parents || []) {
			if (!byParent.has(parent.id)) {
				byParent.set(parent.id, {
					parent,
					options: new Set(),
				});
			}
			byParent.get(parent.id).options.add(label);
		}
	}

	/** signatureKey -> { selectedOptions, count, parents } */
	const bySignature = new Map();
	for (const { parent, options } of byParent.values()) {
		const selectedOptions = [...options].sort((a, b) =>
			a.localeCompare(b, 'vi')
		);
		const signatureKey = selectedOptions.join('\u0001');
		if (!bySignature.has(signatureKey)) {
			bySignature.set(signatureKey, {
				signatureKey,
				selectedOptions,
				count: 0,
				parents: [],
			});
		}
		const row = bySignature.get(signatureKey);
		row.count++;
		row.parents.push(parent);
	}

	return Array.from(bySignature.values()).sort((a, b) =>
		a.signatureKey.localeCompare(b.signatureKey, 'vi')
	);
});

const redirectParentList = () => {
	if (showListParent.value) {
		showListParent.value = false;
	} else router.push('/survey');
};

const openSendToParentModal = () => {
	type.value = 'send';
	sideOverShow.value = true;
};

const success = async () => {
	fetchSurveyDetail();
	sideOverShow.value = false;
	modalIsOpen.value = false;
};

const viewParentAnswer = (parentId) => {
	tab.value = 2;
	parentSelected.value = parentsSurvey.value.find(
		(parent) => parent.parent?.id === parentId
	);
};
</script>
<template>
	<div v-if="sideOverShow" :class="sideOverShow ? 'side-over--active' : ''">
		<div
			class="fixed h-screen w-screen top-0 left-0 bg-black opacity-20 z-30"
			@click="sideOverShow = false"
		></div>
		<SendToParent
			v-if="type === 'send'"
			type="'send'"
			@close="sideOverShow = false"
			@success="success"
			:survey="survey"
		/>
	</div>
	<DaModalFrame v-model:open="modalIsOpen">
		<UpdateModal
			@close="modalIsOpen = false"
			:id="id"
			:type="modalType"
			:status="survey.status"
			@success="success"
			:surveyName="survey.name"
		/>
	</DaModalFrame>
	<div class="px-12 md-down:px-4 p-10">
		<div v-if="isLoading">
			<Loading />
		</div>
		<div v-else>
			<div
				class="flex items-center gap-2 text-sm text-neutral-500 font-semibold mb-4"
			>
				<div class="cursor-pointer" @click="router.push('/survey')">
					Phiếu khảo sát
				</div>
				<img alt="" src="@/assets/images/icons/arrow-right.png" />
				<div
					:class="showListParent ? 'cursor-pointer' : ''"
					@click="showListParent = false"
				>
					Chi tiết
				</div>
				<img
					v-if="showListParent"
					alt=""
					src="@/assets/images/icons/arrow-right.png"
				/>
				<div v-if="showListParent">
					Danh sách phụ huynh nhận được khảo sát
				</div>
			</div>
			<div class="flex flex-nowrap justify-between items-center mb-2">
				<div>
					<div
						class="text-neutral-700 font-bold whitespace-pre-wrap flex flex-nowrap items-center text-4xl gap-3"
					>
						<img
							src="@/assets/images/icons/arrow-right.png"
							alt=""
							class="w-5 h-5 cursor-pointer"
							style="transform: rotate(180deg)"
							@click="redirectParentList"
						/>
						<img
							src="@/assets/images/icons/form-evaluation.png"
							class="w-7 h-7"
						/>
						<div>{{ survey?.name }}</div>
					</div>
					<div class="text-green-500 text-xs font-semibold">
						TẠO BỞI
						<span class="text-blue-600 text-sm">{{
							survey?.created_by_staff?.full_name
						}}</span>
						LÚC
						{{ $filters.formatDateTime(survey.inserted_at) }}
						|
						<span v-if="survey?.status === 'draft'">BẢN NHÁP</span>
						<span v-else-if="survey?.status === 'published'">
							<span class="text-blue-600 text-sm">{{
								survey?.updated_by_staff?.full_name
							}}</span>
							ĐÃ GỬI KS TỚI PH VÀO
							{{ $filters.formatDateTime(survey.updated_at) }}
						</span>
						<span
							v-else-if="survey?.status === 'closed'"
							class="text-sienna-500"
						>
							ĐÃ THU HỒI
						</span>
					</div>
				</div>
				<div class="flex items-center gap-2" v-if="!showListParent">
					<button
						v-if="survey.status === 'draft'"
						class="button-p btn--ghost w-fit px-4"
						type="button"
						@click="$router.push(`/survey/create?id=${id}`)"
					>
						Chỉnh sửa
					</button>
					<button
						v-if="survey.status === 'draft'"
						class="button-p w-fit px-4"
						type="button"
						@click="openSendToParentModal"
					>
						Gửi phụ huynh
					</button>
					<button
						v-else
						class="button-p btn--ghost w-fit px-4"
						type="button"
						@click="showListParent = true"
					>
						Xem danh sách phụ huynh
					</button>
					<Menu as="div" class="relative">
						<div>
							<MenuButton
								class="rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
							>
								<span class="sr-only">Open user menu</span>
								<svg
									fill="none"
									height="20"
									viewBox="0 0 20 20"
									width="20"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										clip-rule="evenodd"
										d="M11.875 16.25C11.875 17.2855 11.0355 18.125 10 18.125C8.96447 18.125 8.125 17.2855 8.125 16.25C8.125 15.2145 8.96447 14.375 10 14.375C11.0355 14.375 11.875 15.2145 11.875 16.25ZM11.875 10C11.875 11.0355 11.0355 11.875 10 11.875C8.96447 11.875 8.125 11.0355 8.125 10C8.125 8.96447 8.96447 8.125 10 8.125C11.0355 8.125 11.875 8.96447 11.875 10ZM11.875 3.75C11.875 4.78553 11.0355 5.625 10 5.625C8.96447 5.625 8.125 4.78553 8.125 3.75C8.125 2.71447 8.96447 1.875 10 1.875C11.0355 1.875 11.875 2.71447 11.875 3.75Z"
										fill="#94A3B8"
										fill-rule="evenodd"
									/>
								</svg>
							</MenuButton>
						</div>
						<transition
							enter-active-class="transition ease-out duration-200"
							enter-from-class="transform opacity-0 scale-95"
							enter-to-class="transform opacity-100 scale-100"
							leave-active-class="transition ease-in duration-75"
							leave-from-class="transform opacity-100 scale-100"
							leave-to-class="transform opacity-0 scale-95"
						>
							<MenuItems
								class="origin-top-right absolute right-0 mt-2 w-60 rounded-md py-1 bg-white focus:outline-none z-10 shadow-sm border border-yellow-100"
							>
								<MenuItem
									v-slot="{ active }"
									v-if="survey.status === 'published'"
								>
									<div
										@click="openUpdateModal('create')"
										:class="active ? '' : ''"
										class="p-2 px-4 bg-white z-10 text-gray-600 hover:bg-neutral-50 flex items-center gap-4 cursor-pointer"
									>
										<img
											src="@/assets/images/icons/plus-circle-fill.png"
											alt=""
											class="w-4 h-4"
										/>
										<span>Tạo biểu mẫu</span>
									</div>
								</MenuItem>
								<MenuItem
									v-slot="{ active }"
									v-if="survey.status === 'published'"
								>
									<div
										@click="openSendToParentModal"
										:class="active ? '' : ''"
										class="p-2 px-4 bg-white z-10 text-gray-600 hover:bg-neutral-50 flex items-center gap-4 cursor-pointer"
									>
										<img
											src="@/assets/images/icons/send.png"
											alt=""
											class="w-4 h-4"
										/>
										<span>Gửi lại khảo sát</span>
									</div>
								</MenuItem>
								<MenuItem
									v-slot="{ active }"
									v-if="survey.status === 'published'"
								>
									<div
										@click="openUpdateModal('recall')"
										:class="active ? '' : ''"
										class="p-2 px-4 bg-white z-10 text-gray-600 hover:bg-neutral-50 flex items-center gap-4 cursor-pointer"
									>
										<img
											src="@/assets/images/icons/recall.png"
											alt=""
											class="w-4 h-4"
										/>
										<span>Thu hồi khảo sát</span>
									</div>
								</MenuItem>
								<MenuItem v-slot="{ active }">
									<div
										@click="openUpdateModal('deleteSurvey')"
										:class="active ? '' : ''"
										class="p-2 px-4 bg-white z-10 text-gray-600 hover:bg-neutral-50 flex items-center gap-4 cursor-pointer"
									>
										<img
											src="@/assets/images/icons/trash.png"
											alt=""
											class="w-4 h-4"
										/>
										<span>Xoá</span>
									</div>
								</MenuItem>
							</MenuItems>
						</transition>
					</Menu>
				</div>
			</div>
			<div v-if="showListParent">
				<div class="mt-10">
					<div class="flex flex-col">
						<div class="-my-2 sm:-mx-6 lg:-mx-8">
							<div
								class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8"
							>
								<div class="shadow sm:rounded-lg">
									<table
										class="min-w-full divide-y text-neutral-200"
									>
										<thead class="bg-neutral-100 h-16">
											<tr>
												<th
													scope="col"
													class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap w-16"
												>
													STT
												</th>
												<th
													scope="col"
													class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
												>
													Tên hiển thị
												</th>
												<th
													scope="col"
													class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
												>
													mô hình
												</th>
												<th
													scope="col"
													class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
												>
													PH đã xem
												</th>
												<th
													scope="col"
													class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
												>
													PH đã hoàn thành
												</th>
											</tr>
										</thead>
										<tbody>
											<tr
												v-for="(
													parent, index
												) in listSurveyByParent"
												:key="parent.id"
												:class="
													index % 2 === 0
														? 'bg-white'
														: 'bg-neutral-50'
												"
											>
												<td
													class="px-6 py-3 whitespace-nowrap text-neutral-700"
												>
													{{ index + 1 }}
												</td>
												<td
													class="px-6 py-3 whitespace-nowrap text-neutral-700 min-w-40"
												>
													<div
														class="flex items-center gap-2"
													>
														<div>
															<img
																v-if="
																	parent
																		?.profile_photo
																		?.w200
																"
																:src="
																	parent
																		?.profile_photo
																		?.w200
																"
																alt=""
																class="shadow-sm inline-block h-9 w-9 rounded-full"
															/>
															<span
																v-else
																:style="{
																	background:
																		parent
																			?.profile_photo
																			?.default
																			?.color,
																}"
																class="inline-flex items-center justify-center h-9 w-9 rounded-full"
															>
																<span
																	class="font-medium leading-none text-white text-xs"
																	>{{
																		parent
																			?.profile_photo
																			?.default
																			?.initial
																	}}</span
																>
															</span>
														</div>
														<div
															class="ml-4 text-blue-600 font-semibold truncate"
														>
															{{ parent.name }}
														</div>
													</div>
												</td>
												<td
													class="px-6 py-3 whitespace-nowrap"
												>
													<div
														class="flex items-center"
													>
														<div
															v-if="
																parent?.has_personal_class
															"
															class="p-2"
														>
															<img
																alt=""
																class="w-5 h-5"
																src="@/assets/images/icons/class-teacher.png"
															/>
														</div>
														<div
															v-if="
																parent?.has_day_care_class
															"
															class="p-2"
														>
															<img
																alt=""
																class="w-5 h-5"
																src="@/assets/images/icons/spoon.png"
															/>
														</div>
														<div
															v-if="
																parent?.has_group_class
															"
															class="p-2"
														>
															<img
																alt=""
																class="w-5 h-5"
																src="@/assets/images/icons/group.png"
															/>
														</div>
													</div>
												</td>
												<td
													class="px-6 py-3 whitespace-nowrap text-neutral-700"
												>
													<div v-if="parent.seen">
														Đã xem
													</div>
												</td>
												<td
													class="px-6 py-3 whitespace-nowrap text-neutral-500"
												>
													<div
														class="flex items-center gap-2"
														v-if="parent.submitted"
													>
														<img
															src="@/assets/images/icons/green-tick.png"
															alt=""
															class="w-4 h-4"
														/>
														<div class="text-sm">
															Đã hoàn thành
														</div>
													</div>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div v-else class="max-w-3xl mx-auto">
				<div class="w-full mb-6" v-if="survey.status !== 'draft'">
					<div class="border-b border-neutral-200">
						<nav
							aria-label="Tabs"
							class="-mb-px flex space-x-8 tabs"
						>
							<div
								v-for="(t, index) in tabs"
								:key="index"
								:class="
									tabSelected === index
										? 'border-b-2 text-yellow-700 border-yellow-500'
										: 'text-neutral-500'
								"
								class="group border-transparent hover:text-yellow-700 hover:border-yellow-500 whitespace-nowrap py-3 px-1 border-b-2 font-semibold cursor-pointer"
								@click="tabSelected = index"
							>
								<div class="flex items-center gap-2">
									<div>{{ t.name }}</div>
									<div
										v-if="index === 1"
										class="rounded-xl py-0.5 px-2.5 font-medium text-xs group-hover:bg-yellow-100 group-hover:text-yellow-700"
										:class="
											tabSelected === 1
												? 'bg-yellow-100 text-yellow-700'
												: 'bg-neutral-200 text-neutral-600'
										"
									>
										{{ surveyResult?.total_submitted }}
									</div>
								</div>
							</div>
						</nav>
					</div>
				</div>
				<ListQuestion v-if="tabSelected === 0" :survey="survey" />
				<div v-if="tabSelected === 1">
					<div class="tabs flex mb-10 gap-4">
						<div v-for="(t, index) in tabAnswer" :key="t">
							<div
								:class="{
									'tab-selected': tab === index,
								}"
								class="flex items-center justify-center cursor-pointer py-2 px-3 text-neutral-600"
								@click="tab = index"
							>
								{{ t.name }}
							</div>
						</div>
					</div>
					<div v-if="tab === 0">
						<div class="flex flex-col">
							<div
								v-for="(
									question, questionIndex
								) in surveyResult?.questions"
								:key="question.id"
								:class="{
									'mt-10':
										questionIndex !== 0 &&
										question.question_type === 'section',
									'mt-4': questionIndex !== 0,
								}"
							>
								<div
									class=""
									v-if="question.question_type === 'section'"
								>
									<Section :title="question.title" />
								</div>
								<div
									class="bg-white rounded-2xl shadow-sm p-4"
									v-if="question.question_type !== 'section'"
								>
									<div
										class="text-neutral-600 font-semibold whitespace-pre-wrap text-lg"
									>
										{{ question.title }}
									</div>
									<div
										class="text-green-500 text-xs font-semibold mt-1"
									>
										{{ question.answer_count }} câu trả lời
									</div>

									<div
										class="mt-4 w-full"
										v-if="
											question.question_type ===
											'multiple_choice'
										"
									>
										<Pie
											:series="
												question.apex_chart?.series
											"
											:labels="
												question.apex_chart?.labels
											"
											:options="question.options"
										/>
									</div>
									<div
										class="mt-4 w-full"
										v-if="
											question.question_type ===
											'text_entry'
										"
									>
										<TextList
											:responses="question.responses"
										/>
									</div>
									<div
										class="mt-4 w-full"
										v-if="
											question.question_type ===
											'checkbox'
										"
									>
										<Bar :data="question.apex_chart" />
									</div>
									<div
										class="mt-4 w-full"
										v-if="question.question_type === 'grid'"
									>
										<StackedBar
											:data="question.apex_chart"
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div v-if="tab === 1">
						<div
							class="flex items-center justify-end mb-10 font-semibold text-neutral-600 text-sm"
						>
							<div
								class="flex items-center border-box rounded-md overflow-hidden"
							>
								<div
									class="flex items-center justify-center cursor-pointer w-10 h-10 bg-white border-box-right"
									@click="decrease"
								>
									<img
										src="@/assets/images/icons/arrow-right.png"
										alt=""
										style="transform: rotate(180deg)"
									/>
								</div>
								<div class="flex items-center">
									<div
										class="flex items-center justify-center w-10 h-10 bg-white"
									>
										{{ indexNumber }}
									</div>
									<div
										class="flex items-center justify-center w-10 h-10 bg-white"
									>
										/
									</div>
									<div
										class="flex items-center justify-center w-10 h-10 bg-white"
									>
										{{ surveyQuestions.length }}
									</div>
								</div>
								<div
									class="flex items-center justify-center cursor-pointer w-10 h-10 bg-white border-box-left"
									@click="increase"
								>
									<img
										src="@/assets/images/icons/arrow-right.png"
										alt=""
									/>
								</div>
							</div>
						</div>
						<div class="bg-white rounded-2xl shadow-sm p-8">
							<div
								class="text-neutral-600 font-semibold whitespace-pre-wrap text-lg mb-4"
							>
								{{ surveyQuestions[indexNumber - 1]?.title }}
							</div>
							<div
								v-if="
									surveyQuestions[indexNumber - 1]
										?.question_type === 'multiple_choice'
								"
							>
								<Multiple
									:answers="
										surveyQuestions[indexNumber - 1]
											?.content?.options
									"
									:disabled="true"
								/>
							</div>
							<div
								v-if="
									surveyQuestions[indexNumber - 1]
										?.question_type === 'checkbox'
								"
							>
								<Checkbox
									:answers="
										surveyQuestions[indexNumber - 1]
											?.content?.options
									"
									:disabled="true"
								/>
							</div>
							<div
								v-if="
									surveyQuestions[indexNumber - 1]
										?.question_type === 'grid'
								"
							>
								<Grid
									:content="
										surveyQuestions[indexNumber - 1]
											?.content
									"
									:disabled="true"
									:clickable-rows="true"
									:selected-row="selectedGridRow"
									@select-row="selectedGridRow = $event"
								/>
							</div>
							<div
								v-if="
									surveyQuestions[indexNumber - 1]
										?.question_type === 'text_entry'
								"
							>
								<TextEntry
									:placeholder="
										surveyQuestions[indexNumber - 1]
											?.content?.placeholder
									"
									:disabled="true"
								/>
							</div>
							<div
								class="text-green-500 text-xs font-semibold mt-4"
							>
								{{
									surveyResult.questions.filter(
										(question) =>
											question.question_type !== 'section'
									)[indexNumber - 1]?.answer_count
								}}
								CÂU TRẢ LỜI
							</div>
							<TextList
								:responses="answers.response_groups"
								v-if="answers.question_type === 'text_entry'"
								class="mt-4"
							/>
							<div
								v-if="
									answers.question_type === 'grid' &&
									selectedGridRowData
								"
								class="flex flex-col gap-4 mt-4"
							>
								<div
									v-for="col in selectedGridRowData.columns.filter(
										(c) => c.count > 0
									)"
									:key="col.column"
									class="flex items-center justify-between"
								>
									<div class="flex items-center gap-2">
										<img
											src="@/assets/images/icons/radio.png"
											alt=""
											class="w-5 h-5"
										/>
										<div class="text-neutral-600 text-sm">
											{{ col.column }}
										</div>
									</div>
									<div
										v-if="col.parents?.length"
										class="relative"
									>
										<Menu as="div" class="relative">
											<MenuButton
												class="flex items-center gap-2 cursor-pointer focus:outline-none"
											>
												<div
													class="text-yellow-700 text-sm"
												>
													{{ col.count }} câu trả lời
												</div>
												<img
													src="@/assets/images/icons/yellow-arrow-right.png"
													alt=""
													class="w-4 h-4"
												/>
											</MenuButton>
											<transition
												enter-active-class="transition ease-out duration-200"
												enter-from-class="transform opacity-0 scale-95"
												enter-to-class="transform opacity-100 scale-100"
												leave-active-class="transition ease-in duration-75"
												leave-from-class="transform opacity-100 scale-100"
												leave-to-class="transform opacity-0 scale-95"
											>
												<MenuItems
													class="origin-top-right absolute right-0 mt-2 min-w-[12rem] rounded-lg bg-white py-2 shadow-lg ring-1 ring-black/5 focus:outline-none z-20"
												>
													<MenuItem
														v-for="parent in col.parents"
														:key="parent.id"
														v-slot="{ active }"
													>
														<div
															:class="[
																active
																	? 'bg-neutral-50'
																	: '',
																'px-4 py-2 text-sm text-neutral-700',
															]"
															class="whitespace-nowrap cursor-pointer"
															@click="
																viewParentAnswer(
																	parent.id
																)
															"
														>
															{{ parent.label }}
														</div>
													</MenuItem>
												</MenuItems>
											</transition>
										</Menu>
									</div>
									<div v-else class="flex items-center gap-2">
										<div class="text-yellow-700 text-sm">
											{{ col.count }} câu trả lời
										</div>
										<img
											src="@/assets/images/icons/yellow-arrow-right.png"
											alt=""
											class="w-4 h-4"
										/>
									</div>
								</div>
							</div>
							<div
								v-else-if="answers.question_type === 'checkbox'"
								class="flex flex-col gap-4 mt-4"
							>
								<div
									v-for="(
										row, rowIndex
									) in checkboxParentAnswerRows"
									:key="row.signatureKey"
									:class="{
										'border-b border-neutral-200 pb-4':
											rowIndex !==
											checkboxParentAnswerRows.length - 1,
									}"
								>
									<div
										class="flex items-center justify-between gap-6"
									>
										<div
											class="flex flex-col gap-3 min-w-0"
										>
											<div
												v-for="(
													opt, oi
												) in row.selectedOptions"
												:key="`${row.signatureKey}-${oi}-${opt}`"
												class="flex items-center gap-2"
											>
												<CheckboxTemplate
													:value="true"
												/>
												<div
													class="text-neutral-600 text-sm"
												>
													{{ opt }}
												</div>
											</div>
										</div>
										<div
											v-if="row.parents?.length"
											class="relative shrink-0"
										>
											<Menu as="div" class="relative">
												<MenuButton
													class="flex items-center gap-2 cursor-pointer focus:outline-none"
												>
													<div
														class="text-yellow-700 text-sm"
													>
														{{ row.count }} câu trả
														lời
													</div>
													<img
														src="@/assets/images/icons/yellow-arrow-right.png"
														alt=""
														class="w-4 h-4"
													/>
												</MenuButton>
												<transition
													enter-active-class="transition ease-out duration-200"
													enter-from-class="transform opacity-0 scale-95"
													enter-to-class="transform opacity-100 scale-100"
													leave-active-class="transition ease-in duration-75"
													leave-from-class="transform opacity-100 scale-100"
													leave-to-class="transform opacity-0 scale-95"
												>
													<MenuItems
														class="origin-top-right absolute right-0 mt-2 min-w-[12rem] rounded-lg bg-white py-2 shadow-lg ring-1 ring-black/5 focus:outline-none z-20"
													>
														<MenuItem
															v-for="parent in row.parents"
															:key="parent.id"
															v-slot="{ active }"
														>
															<div
																:class="[
																	active
																		? 'bg-neutral-50'
																		: '',
																	'px-4 py-2 text-sm text-neutral-700 whitespace-nowrap',
																]"
																class="whitespace-nowrap cursor-pointer"
																@click="
																	viewParentAnswer(
																		parent.id
																	)
																"
															>
																{{
																	parent.label
																}}
															</div>
														</MenuItem>
													</MenuItems>
												</transition>
											</Menu>
										</div>
										<div
											v-else
											class="flex items-center gap-2 shrink-0"
										>
											<div
												class="text-yellow-700 text-sm"
											>
												{{ row.count }} câu trả lời
											</div>
											<img
												src="@/assets/images/icons/yellow-arrow-right.png"
												alt=""
												class="w-4 h-4"
											/>
										</div>
									</div>
								</div>
							</div>
							<div
								v-else-if="
									answers.question_type === 'multiple_choice'
								"
								class="flex flex-col gap-4 mt-4"
							>
								<div
									v-for="answer in answers?.response_groups.filter(
										(answer) => answer.count > 0
									)"
									:key="answer.option"
								>
									<div
										class="flex items-center justify-between"
									>
										<div class="flex items-center gap-2">
											<img
												src="@/assets/images/icons/radio.png"
												alt=""
												class="w-5 h-5"
											/>
											<div
												class="text-neutral-600 text-sm"
											>
												{{ answer.option }}
											</div>
										</div>

										<div
											v-if="answer.parents?.length"
											class="relative shrink-0"
										>
											<Menu as="div" class="relative">
												<MenuButton
													class="flex items-center gap-2 cursor-pointer focus:outline-none"
												>
													<div
														class="text-yellow-700 text-sm"
													>
														{{ answer.count }} câu
														trả lời
													</div>
													<img
														src="@/assets/images/icons/yellow-arrow-right.png"
														alt=""
														class="w-4 h-4"
													/>
												</MenuButton>
												<transition
													enter-active-class="transition ease-out duration-200"
													enter-from-class="transform opacity-0 scale-95"
													enter-to-class="transform opacity-100 scale-100"
													leave-active-class="transition ease-in duration-75"
													leave-from-class="transform opacity-100 scale-100"
													leave-to-class="transform opacity-0 scale-95"
												>
													<MenuItems
														class="origin-top-right absolute right-0 mt-2 min-w-[12rem] rounded-lg bg-white py-2 shadow-lg ring-1 ring-black/5 focus:outline-none z-20"
													>
														<MenuItem
															v-for="parent in answer.parents"
															:key="parent.id"
															v-slot="{ active }"
														>
															<div
																:class="[
																	active
																		? 'bg-neutral-50'
																		: '',
																	'px-4 py-2 text-sm text-neutral-700',
																]"
																class="whitespace-nowrap cursor-pointer"
																@click="
																	viewParentAnswer(
																		parent.id
																	)
																"
															>
																{{
																	parent.label
																}}
															</div>
														</MenuItem>
													</MenuItems>
												</transition>
											</Menu>
										</div>
										<div
											v-else
											class="flex items-center gap-2 shrink-0"
										>
											<div
												class="text-yellow-700 text-sm"
											>
												{{ answer.count }} câu trả lời
											</div>
											<img
												src="@/assets/images/icons/yellow-arrow-right.png"
												alt=""
												class="w-4 h-4"
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div v-if="tab === 2">
						<div
							class="flex items-center gap-2 mb-10 font-semibold text-neutral-600 text-sm"
						>
							<Multiselect
								v-model="parentSelected"
								:options="parentsSurvey"
								label="text"
								track-by="id"
								placeholder="Chọn phụ huynh"
								selectLabel=""
								selectedLabel=""
								deselectLabel=""
								:allow-empty="false"
							>
								<template #noResult>
									Không tìm thấy kết quả
								</template>
								<template v-slot:option="props">
									<div class="flex flex-nowrap items-center">
										<div>
											{{
												props.option.parent?.label ??
												props.option.text
											}}
										</div>
									</div>
								</template>
								<template v-slot:singleLabel="{ option }">
									<span>{{
										option.parent?.label ?? option.text
									}}</span>
								</template>
							</Multiselect>
							<!-- <div
							class="flex items-center border-box rounded-md overflow-hidden"
						>
							<div
								class="flex items-center justify-center cursor-pointer w-10 h-10 bg-white border-box-right"
								@click="decreaseParent"
							>
								<img
									src="@/assets/images/icons/arrow-right.png"
									alt=""
									style="transform: rotate(180deg)"
								/>
							</div>
							<div class="flex items-center">
								<div
									class="flex items-center justify-center w-10 h-10 bg-white"
								>
									{{ parentIndexNumber }}
								</div>
								<div
									class="flex items-center justify-center w-10 h-10 bg-white"
								>
									/
								</div>
								<div
									class="flex items-center justify-center w-10 h-10 bg-white"
								>
									{{ parentsSurvey.length }}
								</div>
							</div>
							<div
								class="flex items-center justify-center cursor-pointer w-10 h-10 bg-white border-box-left"
								@click="increaseParent"
							>
								<img
									src="@/assets/images/icons/arrow-right.png"
									alt=""
								/>
							</div>
						</div> -->
						</div>
						<div class="flex flex-col gap-8">
							<div
								v-for="question in surveyResultDetail"
								:key="question.id"
							>
								<div
									v-if="question.question_type === 'section'"
								>
									<Section :title="question.title" />
								</div>
								<div
									class="bg-white rounded-2xl shadow-sm p-4"
									v-if="question.question_type !== 'section'"
								>
									<div
										class="text-neutral-600 font-semibold text-lg mb-4"
									>
										{{ question.title }}
									</div>
									<Multiple
										v-if="
											question.question_type ===
											'multiple_choice'
										"
										:answers="question.content?.options"
										:disabled="true"
										:group-name="question.id"
										:response="
											question.response?.selected_option
										"
									/>
									<Checkbox
										v-if="
											question.question_type ===
											'checkbox'
										"
										:answers="question.content?.options"
										:disabled="true"
										:response="
											question.response?.selected_options
										"
										:otherText="question.response?.other_text"
									/>
									<Grid
										v-if="question.question_type === 'grid'"
										:content="question.content"
										:disabled="true"
										:group-name="question.id"
										:response="question.response?.rows"
									/>
									<TextEntry
										v-if="
											question.question_type ===
											'text_entry'
										"
										:placeholder="
											question.content?.placeholder
										"
										:disabled="true"
										:response="question.response?.text"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<style scoped>
.tab-selected {
	border-radius: 8px;
	background: #e5a42b;
	box-shadow: 0px 3px 8px -1px #3232470d;
	color: #fff;
}
.border-box {
	border: 1px solid #e1e7ef;
}
.border-box-left {
	border-left: 1px solid #e1e7ef;
}
.border-box-right {
	border-right: 1px solid #e1e7ef;
}
</style>